import type { ReactElement } from "react";

import { Flex, Stack } from "@similarweb/ui-components/foundations";

import type { FiltersCategorizedGroup } from "../../types/category";
import type { FilterCardLayout } from "../../types/filter";
import { getCategorySectionDataProps } from "../../utils/categorySection";
import { FilterCardsGridSection } from "../FilterCardsGridSection/FilterCardsGridSection";
import { FilterCategorySectionTitle } from "../FilterCategorySectionTitle/FilterCategorySectionTitle";

import { StyledCategorySection } from "./styles";

type GridItem = { filterCardLayout?: FilterCardLayout };

type Props<TItem extends GridItem> = {
    permanentFilters: readonly TItem[];
    groups: readonly FiltersCategorizedGroup<TItem>[];
    renderFilterCard: (item: TItem) => ReactElement;
};

export const CategorizedFiltersGrid = <TItem extends GridItem>({
    permanentFilters,
    groups,
    renderFilterCard,
}: Props<TItem>) => {
    return (
        <>
            {permanentFilters.length > 0 ? (
                <StyledCategorySection>
                    <Stack gap="8px">{permanentFilters.map(renderFilterCard)}</Stack>
                </StyledCategorySection>
            ) : null}

            {groups.map(({ category, filters }) => {
                const HeaderAction = category.headerAction;
                const EmptyContent = category.emptyContent;

                return (
                    <StyledCategorySection
                        key={category.id}
                        {...getCategorySectionDataProps(category.id)}
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            gap="8px"
                        >
                            <FilterCategorySectionTitle
                                category={category}
                                count={filters.length}
                            />

                            {HeaderAction ? <HeaderAction /> : null}
                        </Flex>

                        {filters.length > 0 ? (
                            <FilterCardsGridSection
                                items={filters}
                                renderItem={renderFilterCard}
                            />
                        ) : EmptyContent ? (
                            <EmptyContent />
                        ) : null}
                    </StyledCategorySection>
                );
            })}
        </>
    );
};
